import type {
  MissionArea,
  AreaKind,
  AreaGeometry,
  UserMembership,
  OrgTreeNode,
  UnitLocation,
  UnitMember,
  InviteSummary,
  LaunchComplex,
  LaunchFocus,
  LocationKind,
  MembershipStatus,
} from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { authHeaders, fetchJson } from '@/services/api-client';

/** An organization summary for the join/browse screen */
export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
}

/** Fields required to create a location */
export interface NewLocationInput {
  name: string;
  kind: LocationKind;
  lat: number;
  lng: number;
  zoom: number;
  /** Marker glyph key from the curated icon catalog; omitted = kind-derived */
  icon?: string;
}

/** Unit-profile fields an admin may update */
export interface ProfileUpdate {
  launchFocus?: LaunchFocus;
  plugins?: string[];
  corePanelFlags?: Record<string, boolean>;
  mapView?: { lat: number; lng: number; zoom: number };
  feedScope?: { bbox: { west: number; south: number; east: number; north: number } };
  emblem?: string | null;
}

/** Result of minting an invite (the code is shown once) */
export interface MintedInvite {
  id: string;
  code: string;
}

/** Send a JSON body with a method and parse the response, throwing on failure */
async function sendJson<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(
    `${CLIENT_CONFIG.apiBase}${url}`,
    authHeaders({
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  );
  if (!res.ok) throw new Error(`${method} ${url} failed: ${res.status}`);
  return (await res.json()) as T;
}

/** Typed workspace/org API client used by queries and panels. */
export const workspaceClient = {
  /** The caller's memberships (drives the gate) */
  getMemberships(): Promise<UserMembership[]> {
    return fetchJson<UserMembership[]>('/api/orgs/me', []);
  },

  /** All organizations for the join/browse screen */
  getOrgs(): Promise<OrgSummary[]> {
    return fetchJson<OrgSummary[]>('/api/orgs', []);
  },

  /** The nested unit tree for an organization */
  getOrgTree(orgId: string): Promise<OrgTreeNode[]> {
    return fetchJson<OrgTreeNode[]>(`/api/orgs/${encodeURIComponent(orgId)}/tree`, []);
  },

  /** A unit's mission areas visible to the caller */
  listAreas(unitId: string): Promise<MissionArea[]> {
    return fetchJson<MissionArea[]>(`/api/units/${encodeURIComponent(unitId)}/areas`, []);
  },

  /** Persist a drawn mission area for a unit */
  createArea(
    unitId: string,
    input: { name: string; kind: AreaKind; geometry: AreaGeometry; color?: string },
  ): Promise<MissionArea> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/areas`, 'POST', input);
  },

  /** Delete a mission area */
  deleteArea(unitId: string, areaId: string): Promise<{ ok: boolean }> {
    return sendJson(
      `/api/units/${encodeURIComponent(unitId)}/areas/${encodeURIComponent(areaId)}`,
      'DELETE',
    );
  },

  /** Member↔area assignments for the unit chain (admin) */
  listAreaAssignments(unitId: string): Promise<Array<{ areaId: string; sub: string }>> {
    return fetchJson(`/api/units/${encodeURIComponent(unitId)}/area-assignments`, []);
  },

  /** Assign a member to a mission area (admin) */
  assignArea(unitId: string, areaId: string, sub: string): Promise<{ ok: boolean }> {
    return sendJson(
      `/api/units/${encodeURIComponent(unitId)}/areas/${encodeURIComponent(areaId)}/assignees`,
      'POST',
      { sub },
    );
  },

  /** Remove a member's mission-area assignment (admin) */
  unassignArea(unitId: string, areaId: string, sub: string): Promise<{ ok: boolean }> {
    return sendJson(
      `/api/units/${encodeURIComponent(unitId)}/areas/${encodeURIComponent(areaId)}/assignees/${encodeURIComponent(sub)}`,
      'DELETE',
    );
  },

  /** Redeem a join code → pending membership */
  join(code: string): Promise<{ unitId: string; status: string }> {
    return sendJson('/api/memberships/join', 'POST', { code });
  },

  /** Locations visible for a unit (own + ancestors' shared + personal) */
  getLocations(unitId: string): Promise<UnitLocation[]> {
    return fetchJson<UnitLocation[]>(`/api/units/${encodeURIComponent(unitId)}/locations`, []);
  },

  /** Create a location for a unit */
  createLocation(unitId: string, input: NewLocationInput): Promise<UnitLocation> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/locations`, 'POST', input);
  },

  /** Delete a location */
  deleteLocation(unitId: string, id: string): Promise<{ ok: boolean }> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/locations/${encodeURIComponent(id)}`, 'DELETE');
  },

  /** List a unit's members, optionally by status (admin) */
  getMembers(unitId: string, status?: MembershipStatus): Promise<UnitMember[]> {
    const suffix = status ? `?status=${status}` : '';
    return fetchJson<UnitMember[]>(`/api/units/${encodeURIComponent(unitId)}/memberships${suffix}`, []);
  },

  /** Approve or deny a membership (admin) */
  decideMember(unitId: string, id: string, status: MembershipStatus): Promise<{ ok: boolean }> {
    return sendJson(
      `/api/units/${encodeURIComponent(unitId)}/memberships/${encodeURIComponent(id)}`,
      'PATCH',
      { status },
    );
  },

  /** List a unit's invites (admin) */
  getInvites(unitId: string): Promise<InviteSummary[]> {
    return fetchJson<InviteSummary[]>(`/api/units/${encodeURIComponent(unitId)}/invites`, []);
  },

  /** Mint a join code for a unit (admin) */
  mintInvite(unitId: string, ttlMs: number, maxUses: number): Promise<MintedInvite> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/invites`, 'POST', { ttlMs, maxUses });
  },

  /** Revoke an invite (admin) */
  revokeInvite(unitId: string, id: string): Promise<{ ok: boolean }> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/invites/${encodeURIComponent(id)}`, 'DELETE');
  },

  /** Launch complexes for the reference layer */
  getLaunchComplexes(): Promise<LaunchComplex[]> {
    return fetchJson<LaunchComplex[]>('/api/reference/launch-complexes', []);
  },

  /** Update a unit profile field set (admin / platform admin) */
  updateProfile(unitId: string, patch: ProfileUpdate): Promise<{ ok: boolean }> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/profile`, 'PATCH', patch);
  },

  /** The caller's personal dashboard overrides for a unit */
  getMyView(unitId: string): Promise<{ hiddenPlugins: string[]; panelFlags: Record<string, boolean> }> {
    return fetchJson(`/api/units/${encodeURIComponent(unitId)}/my-view`, { hiddenPlugins: [], panelFlags: {} });
  },

  /** Save the caller's personal dashboard overrides for a unit */
  saveMyView(
    unitId: string,
    view: { hiddenPlugins: string[]; panelFlags: Record<string, boolean> },
  ): Promise<{ ok: boolean }> {
    return sendJson(`/api/units/${encodeURIComponent(unitId)}/my-view`, 'PATCH', view);
  },

  /** The loaded plugin catalog (id + name) for the entitlement editor */
  getPluginCatalog(): Promise<Array<{ id: string; name: string }>> {
    return fetchJson<Array<{ id: string; name: string }>>('/api/reference/plugins', []);
  },
};
